import "../styles/towersTab.css";
import { Economy, LevelCatalog, Pets, petSheet } from "@highmarch/app";
import { UpgradeTarget, cancelQueued, isQueued, queueUpgrade } from "@highmarch/domain";
import { fmtDuration } from "../format";
import { icon } from "../iconsKit";
import { fmtCompact, pillHtml } from "../kit";
import { describeCost } from "../gearUi";
import { portraitImg, resourceIconUrl, towerCardArt } from "../portraits";
import { builderPick, cheapestUpgrade, pickTarget } from "./builderPick";
import { petTilesHtml } from "../pets/petTowerCard";
import { builderRowHtml } from "./builderStrip";
import { economyChanged, economyLink } from "./economyLink";
import { econCue } from "./purchaseFx";
import { refusalText } from "./shopActions";
import { dealsLink } from "../deals/dealsLink";
import { openDeals } from "../deals/dealsSheet";
import { priceHtml } from "./econArt";
import { queueFill, queueRows, startsNow, upgradeAction } from "./queueModel";
import { toast } from "../toast";
import { openSkins, skinsIcon } from "./skinsSheet";
import { skinsLink } from "./skinsLink";
import { currencyLineHtml, sheetHeadHtml, tagHtml } from "../components";
import { notEnoughGold, queueGoldNeeded } from "./getGold";
import { upArrowSvg } from "./badges";
import { UpgradeNudge } from "./upgradeNudge";

/** The Store page that sells builders (Deals page id; shop redesign 2026-10-19). */
const BUILDERS_PAGE = "builders";

/**
 * Towers tab (hub redesign): the Fortress card, the builder row (idle → the suggested upgrade, busy → bar and ETA, or
 * QUEUE when a queue slot is free), the builders / queue / speed-ups counts with "Hire builder n" for gems (M2), the
 * queued upgrades (D4, M3), then the owned roster and the next tower to unlock. Tapping the Fortress, a tower or the
 * builder's Upgrade/View/Queue opens that upgrade sheet. The base and builders used to float over the scene. Skins
 * (FINISH_PLAN WP8): the head's Skins button and the Fortress card's brush open the skin picker (skinsSheet.ts). Pets
 * v2: each pet caught has a card under the Fortress's (its level, its upgrade's state, the up-arrow when one is ready) that opens
 * the pet sheet; the builder row and the queue name and open a pet's job like a tower's.
 */
export class TowersTab {
  private el: HTMLElement | null = null;

  constructor(private readonly parent: HTMLElement, private readonly catalog: LevelCatalog, private readonly now: () => number, private readonly onPick: (towerIndex: number) => void,
              private readonly onPet: (petId: string) => void = () => {}) {}

  /** `isConnected`: the hub's sheet guard (sheetGuard.ts) may have closed this sheet when another one opened. */
  get isOpen(): boolean { return this.el?.isConnected ?? false; }

  /** Towers the player hasn't seen yet wear a NEW tag on their card (news.ts); kept for refreshes while the page is open. */
  private fresh: ReadonlySet<string> = new Set();
  /** The upgrade the Towers tab's arrow was about when the page opened (UpgradeNudge.keyOf): its tile and Upgrade pulse until it starts. */
  private nudged: string | null = null;

  /** `nudged`: the tab's up-arrow was showing, so the page points at that upgrade. */
  open(fresh?: ReadonlySet<string>, nudged?: string | null): void {
    if (fresh) this.fresh = fresh;
    if (nudged !== undefined) this.nudged = nudged;
    this.close();
    const B = this.catalog.balance, p = this.catalog.profile;
    const owned = this.catalog.ownedTowers();
    const pick = builderPick(B, p, owned, this.now());
    // the arrow's upgrade, while it is still the one to start: its tile (or the Fortress card, or the pet) and the builder's Upgrade pulse
    const point = this.nudged !== null && UpgradeNudge.keyOf(pick) === this.nudged && pick.kind === "idle" ? pick.option : null;
    const pointAt = (target: UpgradeTarget, i: number) => point !== null && point.target === target && point.towerIndex === i;
    const baseIndex = B.towers.findIndex((t) => t.isBase);
    const base = B.towers[baseIndex];
    const busyOn = (i: number) => p.jobs.some((j) => (B.towers[i].isBase ? j.target === UpgradeTarget.Base : j.target === UpgradeTarget.Tower && j.towerIndex === i));
    const max = B.tables.maxLevel;
    const freeNow = p.freeBuilders > 0;
    const cards = B.towers.map((t, i) => {
      if (t.isBase || !owned[i]) return "";
      const lv = p.towerLevels[i], busy = busyOn(i);
      const { art, back } = towerCardArt(t.id, 60, icon("tower", 32, "#fff8e6"));
      const q = !busy && isQueued(p, UpgradeTarget.Tower, i);
      const ready = startsNow(upgradeAction(p, UpgradeTarget.Tower, i));
      const state = busy ? `<i class="tc-st busy" aria-hidden="true">${icon("clock", 12)}</i>` : q ? `<i class="tc-st queued" aria-hidden="true">${icon("clock", 12)}</i>` : ready ? upArrowHtml() : "";
      return `<button class="tcard tb${levelBand(lv)}${busy ? " busy" : ""}${q ? " queued" : ""}${ready ? " ready" : ""}${pointAt(UpgradeTarget.Tower, i) ? " nudge" : ""}" data-i="${i}" aria-label="${t.name}, level ${lv}${ready ? ", upgrade ready" : ""}"><div class="art">${back}${art}${this.fresh.has(t.id) ? tagHtml("new") : ""}${state}<span class="tc-lv">${lv >= max ? "MAX" : `Lv ${lv}`}</span></div><b>${t.name}</b></button>`;
    }).join("");
    const next = B.towers.filter((t, i) => !t.isBase && !owned[i]).sort((a, b) => a.unlockLevel - b.unlockLevel)[0];
    const nextCard = next ? `<div class="tcard locked"><div class="art">${towerCardArt(next.id, 60, icon("tower", 32, "#fff8e6")).art}<span class="tc-lk">${icon("lock", 11)} Level ${next.unlockLevel}</span></div><b>${next.name}</b></div>` : "";
    const baseReady = startsNow(upgradeAction(p, UpgradeTarget.Base, baseIndex));
    const fill = queueFill(p);
    const queueOpt = pick.kind === "busy" && fill.used < fill.capacity ? cheapestUpgrade(B, p, owned) : null;
    const queueIndex = queueOpt?.affordable ? queueOpt.towerIndex : -1;
    const queued = queueRows(B, p, this.now());
    const hire = this.hireHtml();
    const sheet = document.createElement("div");
    sheet.className = "sheet panel towers";
    sheet.innerHTML = `
      ${sheetHeadHtml({ title: "Towers", sub: currencyLineHtml([{ key: "gold", icon: resourceIconUrl("gold"), amount: fmtCompact(this.catalog.profile.gold) }]), portrait: icon("tower", 34, "#fff8e6"), extra: this.skinsBtnHtml() })}
      <div class="fortress-wrap${skinsLink() ? " skinned" : ""}"><button class="fortress tb${levelBand(p.baseLevel)}${busyOn(baseIndex) ? " busy" : ""}${baseReady ? " ready" : ""}${pointAt(UpgradeTarget.Base, baseIndex) ? " nudge" : ""}" aria-label="Fortress, level ${p.baseLevel}"><div class="art">${portraitImg(base.id, 92, icon("tower", 30, "#fff8e6"))}</div><div class="text"><b class="disp">Fortress</b><span class="fx-lv">${p.baseLevel >= max ? "MAX" : `Lv ${p.baseLevel}`}${busyOn(baseIndex) ? ` ${icon("clock", 12)}` : ""}</span></div>${baseReady ? upArrowHtml() : ""}${icon("chev", 20)}</button>${skinsLink() ? `<button class="fbrush" aria-label="Fortress skins">${skinsIcon(24)}</button>` : ""}</div>
      ${this.petCardsHtml()}
      <div class="builders"><div class="brow plate">${builderRowHtml(B, p, pick, queueIndex, queueOpt?.affordable ? `Queue next: ${B.towers[queueOpt.towerIndex].isBase ? "Fortress" : B.towers[queueOpt.towerIndex].name} → Lv ${queueOpt.level + 1} · ${describeCost(queueOpt.cost)}` : "")}</div>
        ${queued.length > 0 ? `<div class="qlist">${queued.map((q) => `<div class="qrow">${icon("clock", 14)}<b>${q.name} → Lv ${q.toLevel}</b><small>${q.startsIn > 0 ? `starts in ${fmtDuration(q.startsIn)}` : "starts next"}</small><button class="qx" ${q.target === UpgradeTarget.Pet ? `data-pet="${B.pets.list[q.towerIndex]?.id ?? ""}"` : `data-i="${q.target === UpgradeTarget.Base ? baseIndex : q.towerIndex}"`} aria-label="Open">${icon("chev", 14)}</button></div>`).join("")}</div>` : ""}
        <div class="counts">${pillHtml("builder", `${p.freeBuilders}/${p.buildersOwned}`, "green")}${fill.capacity > 0 ? pillHtml("clock", `${fill.used}/${fill.capacity}`, "blue", false, "qpill") : ""}${p.speedUps.length > 0 ? pillHtml(resourceIconUrl("speedup"), `${p.speedUps.length}`) : ""}${hire}</div></div>
      <div class="roster">${cards}${nextCard}</div>`;
    if (point !== null) {
      sheet.querySelector(".brow .open")?.classList.add("nudge");
      if (point.target === UpgradeTarget.Pet) sheet.querySelector(`.pettile[data-pet="${B.pets.list[point.towerIndex]?.id ?? ""}"]`)?.classList.add("nudge");
    }
    sheet.querySelector<HTMLButtonElement>(".close")!.onclick = () => this.close();
    sheet.querySelector<HTMLButtonElement>(".fortress")!.onclick = () => this.pick(baseIndex);
    // Skins (FINISH_PLAN WP8): the picker on the towers, or on the fortress from its card
    sheet.querySelector<HTMLButtonElement>(".skinsbtn")?.addEventListener("click", () => { this.close(); openSkins(this.parent, { kind: "tower" }); });
    sheet.querySelector<HTMLButtonElement>(".fbrush")?.addEventListener("click", () => { this.close(); openSkins(this.parent, { kind: "fortress" }); });
    sheet.querySelector<HTMLButtonElement>(".brow .open")?.addEventListener("click", () => {
      const t = pickTarget(pick);
      if (t?.target === UpgradeTarget.Pet) this.pet(B.pets.list[t.towerIndex]?.id ?? "");
      else if (t) this.pick(t.target === UpgradeTarget.Base ? baseIndex : t.towerIndex);
    });
    // M30 #26: QUEUE queues it here (paid now, starts when a builder is free) with a toast and Undo — no full sheet.
    sheet.querySelector<HTMLButtonElement>(".brow .queue")?.addEventListener("click", () => this.queue(queueIndex));
    for (const b of sheet.querySelectorAll<HTMLButtonElement>(".qrow .qx")) b.onclick = () => (b.dataset.pet ? this.pet(b.dataset.pet) : this.pick(Number(b.dataset.i)));
    for (const b of sheet.querySelectorAll<HTMLButtonElement>(".pettile[data-pet]")) b.onclick = () => this.pet(b.dataset.pet ?? "");
    sheet.querySelector<HTMLButtonElement>(".hire")?.addEventListener("click", () => this.hire());
    for (const c of sheet.querySelectorAll<HTMLButtonElement>("button.tcard")) c.onclick = () => this.pick(Number(c.dataset.i));
    this.parent.append(sheet);
    this.el = sheet;
  }

  private pick(i: number): void { this.close(); this.onPick(i); }
  private pet(id: string): void { if (!id) return; this.close(); this.onPet(id); }

  /** The pets caught, a card each (Pets v2), under the Fortress's. */
  private petCardsHtml(): string {
    const pets = new Pets(this.catalog), eq = pets.equipped()?.id ?? null, B = this.catalog.balance, p = this.catalog.profile;
    // every pet in roster order: the caught ones level like towers, the rest are silhouettes (Pets v3)
    return petTilesHtml(B.pets.list, new Map(pets.owned().map((pet) => [pet.id, petSheet(B, p, pet, eq)] as const)));
  }

  /** Queues the next level of tower `i` (or the fortress) at once; the toast's Undo cancels it for everything back. */
  private queue(i: number): void {
    const B = this.catalog.balance, p = this.catalog.profile, t = B.towers[i];
    if (!t) return;
    const target = t.isBase ? UpgradeTarget.Base : UpgradeTarget.Tower;
    const q = queueUpgrade(p, target, i, this.now());
    if (q === "not enough gold") { void notEnoughGold(this.parent, queueGoldNeeded(p, target, i), this.parent.querySelector(":scope > .sheet.panel.towers")); return; }
    if (typeof q === "string") { toast(this.parent, refusalText(q)); return; }
    econCue("queue");
    economyChanged();
    this.open();
    toast(this.parent, `Queued: ${t.isBase ? "Fortress" : t.name} → Lv ${q.toLevel}. Starts when a builder is free.`, {
      action: { label: "Undo", onTap: () => { if (cancelQueued(p, q.id) !== null) { economyChanged(); if (this.isOpen) this.open(); } } },
    });
  }

  /** The head's Skins button, with a red count of skins unlocked and not yet seen (hidden before the feature boots). */
  private skinsBtnHtml(): string {
    const link = skinsLink();
    if (!link) return "";
    const n = link.cosmetics.unseen().length;
    return `<button class="skinsbtn" aria-label="Skins">${skinsIcon(20)}<span>Skins</span>${n > 0 ? `<span class="skn">${n}</span>` : ""}</button>`;
  }

  /**
   * The next builder (shop redesign 2026-10-19: builders are real-money packs, Store → Builders): "Builder n" and its
   * price, a tap opens the Store on Builders. Hidden once every builder is hired, without a store, and in the quiet after
   * a loss (no nudges then).
   */
  private hireHtml(): string {
    const l = dealsLink(), eco = economyLink() ? new Economy(this.catalog) : null;
    if (!l || !eco || !eco.offersAllowed("hub", this.now())) return "";
    const page = l.deals.pages(this.now(), l.ctx()).find((x) => x.page.id === BUILDERS_PAGE);
    const o = page?.offers.find((x) => x.blocked !== "sold out");
    if (!o) return "";
    const n = this.catalog.profile.buildersOwned + 1;
    return `<button class="hire ok" aria-label="Hire builder ${n}, ${o.price.label}"><span class="hplus" aria-hidden="true">+</span>${icon("builder", 16)}<span class="ht"><b>Builder ${n}</b></span><span class="btn hireprice">${o.price.label}</span></button>`;
  }

  private hire(): void {
    const l = dealsLink();
    if (l) l.hub = l.hub ?? null;
    if (!openDeals({ page: BUILDERS_PAGE })) toast(this.parent, "Builders are sold in the Store");
  }

  /** Every second while open: the builder row's ETA and bar move without rebuilding; a finished job re-opens the sheet. */
  tick(): void {
    if (!this.isOpen) return;
    const p = this.catalog.profile;
    const pick = builderPick(this.catalog.balance, p, this.catalog.ownedTowers(), this.now());
    const eta = this.el!.querySelector<HTMLElement>(".brow .eta");
    if (pick.kind !== "busy" || !eta) return;
    eta.textContent = fmtDuration(pick.remaining);
    const fill = this.el!.querySelector<HTMLElement>(".brow .fill");
    if (fill) fill.style.width = `${pick.progress * 100}%`;
  }

  refresh(): void { if (this.isOpen) this.open(); }
  close(): void { this.el?.remove(); this.el = null; }
  dispose(): void { this.close(); }
}

/**
 * A level's frame band (UI pass 2026-10-21): the tile's colour climbs as the level does, on the Shop's rarity colours —
 * steel (1–9), green (10–29), blue (30–59), purple (60–99), gold (100–159), red (160+); the top two glow.
 */
export function levelBand(level: number): number {
  return level >= 160 ? 5 : level >= 100 ? 4 : level >= 60 ? 3 : level >= 30 ? 2 : level >= 10 ? 1 : 0;
}

/** The green "upgrade ready" arrow on a tile's corner (an affordable next level and a free builder). */
export function upArrowHtml(): string {
  return `<i class="tc-up" aria-hidden="true">${upArrowSvg(14)}</i>`;
}
